import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RESOURCE_CONFIG } from "@/pages/admin/resourceConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import MediaPicker from "@/components/admin/MediaPicker";
import { Plus, Pencil, Trash2, Loader2, Check, X, Sparkles } from "lucide-react";

const IMAGE_FIELDS = ["cover_image", "image", "avatar", "og_image"];

function emptyFromFields(fields) {
  const obj = {};
  fields.forEach((f) => {
    obj[f.name] = f.type === "boolean" ? false : f.type === "list" ? "" : f.type === "number" ? "" : "";
  });
  return obj;
}

export default function ResourceManager({ resource }) {
  const config = RESOURCE_CONFIG[resource];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/cms/${resource}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyFromFields(config.fields));
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const f = {};
    config.fields.forEach((fld) => {
      const v = item[fld.name];
      f[fld.name] = fld.type === "list" ? (Array.isArray(v) ? v.join(", ") : v || "") : v ?? (fld.type === "boolean" ? false : "");
    });
    setForm(f);
    setDialogOpen(true);
  };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const save = async () => {
    setSaving(true);
    const payload = {};
    config.fields.forEach((fld) => {
      let v = form[fld.name];
      if (fld.type === "list") v = (v || "").split(",").map((x) => x.trim()).filter(Boolean);
      else if (fld.type === "number") v = v === "" || v === null ? 0 : Number(v);
      else if (fld.type === "boolean") v = Boolean(v);
      payload[fld.name] = v;
    });
    try {
      if (editing) await api.put(`/admin/cms/${resource}/${editing.id}`, payload);
      else await api.post(`/admin/cms/${resource}`, payload);
      toast.success(`${config.singular} ${editing ? "updated" : "created"}`);
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/cms/${resource}/${deleteId}`);
      toast.success(`${config.singular} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleteId(null);
    }
  };

  const generateAi = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/blog-draft", { topic: aiTopic });
      const f = {};
      config.fields.forEach((fld) => {
        const v = data[fld.name];
        if (fld.type === "list") f[fld.name] = Array.isArray(v) ? v.join(", ") : v || "";
        else if (fld.type === "boolean") f[fld.name] = Boolean(v);
        else f[fld.name] = v ?? "";
      });
      setForm(f);
      setEditing(null);
      setAiOpen(false);
      setAiTopic("");
      setDialogOpen(true);
      toast.success("Draft generated — review and save");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const generateAndPublish = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      await api.post("/admin/ai/blog-publish", { topic: aiTopic });
      toast.success("Post generated and published!");
      setAiOpen(false);
      setAiTopic("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const renderCell = (item, col) => {
    const v = item[col.key];
    if (col.type === "boolean") {
      return v ? <Check className="h-4 w-4 text-emerald-400" /> : <X className="h-4 w-4 text-slate-600" />;
    }
    return <span className="line-clamp-1">{String(v ?? "")}</span>;
  };

  return (
    <div className="space-y-6" data-testid={`admin-manager-${resource}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">{config.title}</h1>
          <p className="mt-1 text-sm text-slate-400">{items.length} {items.length === 1 ? "item" : "items"}</p>
        </div>
        <div className="flex gap-3">
          {resource === "blog" && (
            <Button onClick={() => setAiOpen(true)} variant="ghost" className="border border-vantage/40 text-vantage hover:bg-vantage/10" data-testid="ai-draft-open">
              <Sparkles className="h-4 w-4" /> AI Draft
            </Button>
          )}
          <Button onClick={openCreate} className="bg-vantage hover:bg-vantage-hover" data-testid={`add-${resource}`}>
            <Plus className="h-4 w-4" /> New {config.singular}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-ink-900">
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-vantage" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {config.columns.map((c) => (
                  <TableHead key={c.key} className="text-slate-400">{c.label}</TableHead>
                ))}
                <TableHead className="text-right text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="border-white/5" data-testid={`row-${item.id}`}>
                  {config.columns.map((c) => (
                    <TableCell key={c.key} className="max-w-[240px] text-slate-200">{renderCell(item, c)}</TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(item)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" data-testid={`edit-${item.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteId(item.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`delete-${item.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={config.columns.length + 1} className="py-10 text-center text-slate-500">No items yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-ink-900 text-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "New"} {config.singular}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            {config.fields.map((fld) => (
              <div key={fld.name} className={fld.full || fld.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="text-slate-300">{fld.label}</Label>
                {fld.type === "textarea" ? (
                  <Textarea
                    rows={fld.rows || 3}
                    value={form[fld.name] ?? ""}
                    onChange={(e) => setField(fld.name, e.target.value)}
                    className="mt-2 border-white/10 bg-white/[0.03]"
                    data-testid={`field-${fld.name}`}
                  />
                ) : fld.type === "boolean" ? (
                  <div className="mt-3 flex items-center gap-3">
                    <Switch checked={!!form[fld.name]} onCheckedChange={(v) => setField(fld.name, v)} data-testid={`field-${fld.name}`} />
                    <span className="text-sm text-slate-400">{form[fld.name] ? "Yes" : "No"}</span>
                  </div>
                ) : (
                  <>
                    <Input
                      type={fld.type === "number" ? "number" : "text"}
                      value={form[fld.name] ?? ""}
                      onChange={(e) => setField(fld.name, e.target.value)}
                      className="mt-2 border-white/10 bg-white/[0.03]"
                      data-testid={`field-${fld.name}`}
                    />
                    {IMAGE_FIELDS.includes(fld.name) && (
                      <div className="flex items-center gap-3">
                        <MediaPicker onSelect={(url) => setField(fld.name, url)} />
                        {form[fld.name] ? (
                          <img src={form[fld.name]} alt="preview" className="mt-2 h-12 w-12 rounded-lg border border-white/10 object-cover" />
                        ) : null}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-vantage hover:bg-vantage-hover" data-testid="save-item">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Draft dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="border-white/10 bg-ink-900 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-vantage" /> AI Blog Assistant</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-slate-300">What should the post be about?</Label>
            <Textarea
              rows={3}
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              placeholder="e.g. How AI automation cuts operational costs for agencies"
              className="mt-2 border-white/10 bg-white/[0.03]"
              data-testid="ai-topic-input"
            />
            <p className="mt-2 text-xs text-slate-500">Generates a full draft with title, content, tags and SEO meta using Claude. Review before publishing.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button onClick={generateAndPublish} disabled={aiLoading || !aiTopic.trim()} variant="ghost" className="border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="ai-generate-publish">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate & Publish"}
            </Button>
            <Button onClick={generateAi} disabled={aiLoading || !aiTopic.trim()} className="bg-vantage hover:bg-vantage-hover" data-testid="ai-generate">
              {aiLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4" /> Generate draft</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="border-white/10 bg-ink-900 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {config.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
